import React from "react";
import { getLang, t } from "../i18n";
import { useStore } from "../store";
import { Link } from "../ui";
import { PublicFooter, PublicHeader } from "./landing";

function Page({ title, children }: { title: string; children: React.ReactNode }) {
  const me = useStore((s) => s.me);
  return (
    <div className={me ? "doc" : "doc doc-public"}>
      {!me && <PublicHeader />}
      <article className="doc-body">
        <h1>{title}</h1>
        {children}
      </article>
      {!me && <PublicFooter />}
    </div>
  );
}

/*
 * NOTE FOR THE OWNER: the privacy policy and terms below are a plain-language
 * first draft written to match how the code actually behaves. They must be
 * reviewed by a lawyer (PDPL in Saudi Arabia, App Store rules) before launch.
 */

export function Help() {
  const ar = getLang() === "ar";
  const qa: [string, string][] = ar
    ? [
        ["كيف أبدأ مباراة مع أصحابي؟", "افتح اللعبة ← اصنع غرفة خاصة ← أرسل الكود لأصحابك. لما يجهزون، اضغط «ابدأ اللعب»."],
        ["انقطع الاتصال وسط المباراة؟", "ارجع للغرفة من الرئيسية وتكمل من نفس المكان. إذا خلص وقت دورك يلعب النظام نقلة تلقائية عنك."],
        ["أين أرى قواعد كل لعبة؟", "القواعد المطبّقة موثّقة في صفحة كل لعبة، والخادم هو الذي يتحقق من كل نقلة، فلا يمكن تجاوزها."],
        ["هل العملات تتحول لفلوس؟", "لا. العملات والهدايا ترفيهية داخل سمرة فقط ولا تُصرف ولا تُحوّل نقدًا."],
        ["شحنت ولم يصل الرصيد؟", "تُضاف العملات فقط بعد تأكيد الدفع من مزوّد الدفع. إذا خُصم منك ولم تصل خلال دقائق راسلنا مع رقم العملية من المحفظة."],
        ["كيف أبلّغ عن لاعب؟", "من قائمة اللاعب (⋯) داخل الغرفة أو من ملفه: «إبلاغ». ويمكنك الكتم أو الحظر فورًا."],
        ["كيف أحذف حسابي؟", "الإعدادات ← احذف حسابي. يُحذف ملفك وأصدقاؤك ورسائلك."],
      ]
    : [
        ["How do I play with friends?", "Open a game → create a private room → send the code. When they're ready, press Start match."],
        ["I lost connection mid-match", "Go back to the room from Home and continue where you left off. If your turn timer runs out, an automatic move is played for you."],
        ["Where are the rules?", "Each game's rules are documented, and the server checks every move, so they can't be bypassed."],
        ["Can coins be cashed out?", "No. Coins and gifts are for fun inside Samra only and can't be exchanged for money."],
        ["I paid but got no coins", "Coins are added only after the payment provider confirms. If you were charged and nothing arrived within minutes, contact us with the payment ID from your wallet."],
        ["How do I report someone?", "Use the player menu (⋯) in a room or their profile → Report. You can also mute or block instantly."],
        ["How do I delete my account?", "Settings → Delete my account. Your profile, friends and messages are removed."],
      ];
  return (
    <Page title={t.help}>
      <dl className="faq">
        {qa.map(([q, a]) => (
          <React.Fragment key={q}>
            <dt>{q}</dt>
            <dd>{a}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p>
        <Link to="/privacy">{t.privacy}</Link> · <Link to="/terms">{t.terms}</Link>
      </p>
    </Page>
  );
}

export function Privacy() {
  const ar = getLang() === "ar";
  return (
    <Page title={t.privacy}>
      {ar ? (
        <>
          <p>مسودة أولى — تخضع لمراجعة قانونية قبل الإطلاق.</p>
          <h2>ما نجمعه</h2>
          <p>البريد الإلكتروني واسم المستخدم والاسم الظاهر، وكلمة المرور مشفّرة بخوارزمية scrypt ولا نطّلع عليها. نسجّل المباريات ونقلاتها لضمان نزاهة اللعب، ورسائل الدردشة داخل الغرف، وعمليات الشحن والهدايا في سجل مالي.</p>
          <h2>المدفوعات</h2>
          <p>لا نخزّن بيانات بطاقتك. يتم الدفع عبر مزوّد الدفع أو Apple، ونحتفظ فقط برقم العملية والمبلغ وحالتها.</p>
          <h2>ما يراه الآخرون</h2>
          <p>اسمك الظاهر وصورتك ومستواك. حالة الاتصال والإحصاءات يمكنك إخفاؤها من الإعدادات.</p>
          <h2>حذف الحساب</h2>
          <p>عند الحذف تُمسح بياناتك الشخصية ورسائلك وصداقاتك. نحتفظ بسجلات المعاملات المالية مجهّلة لأغراض محاسبية ونظامية.</p>
          <h2>التواصل</h2>
          <p>عبر صفحة TechCore الرسمية.</p>
        </>
      ) : (
        <>
          <p>First draft — subject to legal review before launch.</p>
          <h2>What we collect</h2>
          <p>Email, username and display name; your password is stored only as a scrypt hash. We record matches and moves to keep play fair, chat messages inside rooms, and top-ups and gifts in a financial ledger.</p>
          <h2>Payments</h2>
          <p>We never store card details. Payments go through the payment provider or Apple; we keep only the transaction ID, amount and status.</p>
          <h2>What others see</h2>
          <p>Your display name, avatar and level. You can hide your online status and stats in Settings.</p>
          <h2>Deleting your account</h2>
          <p>Deleting removes your personal data, messages and friendships. Financial transaction records are kept anonymised for accounting and legal reasons.</p>
          <h2>Contact</h2>
          <p>Through the official TechCore website.</p>
        </>
      )}
    </Page>
  );
}

export function Terms() {
  const ar = getLang() === "ar";
  return (
    <Page title={t.terms}>
      {ar ? (
        <>
          <p>مسودة أولى — تخضع لمراجعة قانونية قبل الإطلاق.</p>
          <ul>
            <li>العملات الافتراضية والهدايا للترفيه داخل سمرة فقط، لا قيمة نقدية لها ولا تُستبدل ولا تُحوّل بين المستخدمين مقابل مال.</li>
            <li>لا يمكن شراء نتائج المباريات أو الترتيب؛ تُحسب النتائج والخبرة على الخادم.</li>
            <li>يُمنع الغش، والإساءة، ونشر الروابط وأرقام التواصل، وانتحال الشخصيات. قد يؤدي ذلك لإيقاف الحساب.</li>
            <li>المبالغ المستردة تُخصم عملاتها من رصيدك.</li>
            <li>قد نعدّل الألعاب والأسعار والعروض من وقت لآخر، ويُعرض السعر النهائي قبل أي شراء.</li>
          </ul>
        </>
      ) : (
        <>
          <p>First draft — subject to legal review before launch.</p>
          <ul>
            <li>Virtual coins and gifts are for entertainment inside Samra only. They have no cash value and can't be exchanged or traded for money.</li>
            <li>Match results and rankings can't be bought; results and XP are computed on the server.</li>
            <li>Cheating, abuse, posting links or contact numbers, and impersonation are not allowed and may lead to suspension.</li>
            <li>Refunded purchases remove the matching coins from your balance.</li>
            <li>Games, prices and offers may change; the final price is always shown before purchase.</li>
          </ul>
        </>
      )}
    </Page>
  );
}
